/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useContext} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import useMultiSite from './use-multi-site'
import useSiteType from './use-site-type'
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Heading,
    VisuallyHidden,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from './use-toast'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomer,
    useCustomerId,
    useCustomerType,
    useCustomerBaskets,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {CloseIcon} from '../components/custom-icons'
import LoginForm from '../components/login'
import ResetPasswordForm from '../components/reset-password'
import RegisterForm from '../components/register'
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index'
import {noop} from '../utils/utils'
import {
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_ERROR_MESSAGES,
    USER_NOT_FOUND_ERROR
} from '@salesforce/retail-react-app/app/constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {findDialCodeByCountry, removePrefixFromKeys} from '../utils/utils'
import {helpers} from 'commerce-sdk-isomorphic'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {usePasswordContext} from '../custom-contexts'
import {useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useShipToCountry from './use-ship-to-country'
import {IPContext} from './../custom-contexts'

export const LOGIN_VIEW = 'login'
export const REGISTER_VIEW = 'register'
export const PASSWORD_VIEW = 'password'
export const EMAIL_VIEW = 'email'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import useUpdateShopperContext from '@app/hooks/use-update-shopper-context'

/**
 * A constant object representing different views for an authentication modal.
 * Each view contains properties for accessibility and user interface messages.
 *
 * @constant
 * @type {Object}
 * @property {Object} LOGIN_VIEW - Configuration for the login view.
 * @property {ReactElement} LOGIN_VIEW.heading - The heading element for the login view.
 * @property {Object} LOGIN_VIEW.ariaLabel - Aria label for closing the login form.
 * @property {Object} LOGIN_VIEW.liveMessage - Live message for switching to the login modal.
 *
 * @property {Object} REGISTER_VIEW - Configuration for the registration view.
 * @property {ReactElement} REGISTER_VIEW.heading - The heading element for the registration view.
 * @property {Object} REGISTER_VIEW.ariaLabel - Aria label for closing the create account form.
 * @property {Object} REGISTER_VIEW.liveMessage - Live message for switching to the registration modal.
 *
 * @property {Object} PASSWORD_VIEW - Configuration for the forgot password view.
 * @property {ReactElement} PASSWORD_VIEW.heading - The heading element for the forgot password view.
 * @property {Object} PASSWORD_VIEW.ariaLabel - Aria label for closing the forgot password form.
 * @property {Object} PASSWORD_VIEW.liveMessage - Live message for switching to the reset password modal.
 */
const AUTH_MODAL_VIEWS = {
    [LOGIN_VIEW]: {
        heading: <FormattedMessage defaultMessage="Login" id="auth_modal.login.title" />,
        ariaLabel: defineMessage({
            defaultMessage: 'Close login form',
            id: 'auth_modal.login.close'
        }),
        liveMessage: defineMessage({
            defaultMessage: 'Switch to Login Modal',
            id: 'auth_modal.login.live_message'
        })
    },
    [REGISTER_VIEW]: {
        heading: (
            <FormattedMessage defaultMessage="Create an Account" id="auth_modal.register.title" />
        ),
        ariaLabel: defineMessage({
            defaultMessage: 'Close create account form',
            id: 'auth_modal.register.close'
        }),
        liveMessage: defineMessage({
            defaultMessage: 'Switch to Registration Modal',
            id: 'auth_modal.register.live_message'
        })
    },
    [PASSWORD_VIEW]: {
        heading: (
            <FormattedMessage defaultMessage="Forgot password?" id="auth_modal.password.title" />
        ),
        ariaLabel: defineMessage({
            defaultMessage: 'Close forgot password form',
            id: 'auth_modal.password.close'
        }),
        liveMessage: defineMessage({
            defaultMessage: 'Switch to Reset Password Modal',
            id: 'auth_modal.password.live_message'
        })
    }
}

/**
 * Defines a message object for login error notifications.
 * This message is used to inform the user when an error occurs during the login process,
 * specifically when the email or password provided is incorrect.
 *
 * @constant
 * @type {Object}
 * @property {string} defaultMessage - The default message displayed to the user.
 * @property {string} id - The unique identifier for the message, used for localization.
 */
const LOGIN_ERROR = defineMessage({
    defaultMessage: 'Something went wrong. Try again.',
    id: 'auth_modal.error.incorrect_email_or_password'
})

/**
 * AuthModal component provides a modal interface for user authentication, including login, registration,
 * and password reset functionalities. It supports both password-based and passwordless login methods,
 * and integrates with social login providers if enabled.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.initialView=LOGIN_VIEW] - The initial view to display in the modal (LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW).
 * @param {string} [props.initialEmail=''] - The initial email to pre-fill in the form.
 * @param {Function} [props.onLoginSuccess=noop] - Callback function to execute upon successful login.
 * @param {Function} [props.onRegistrationSuccess=noop] - Callback function to execute upon successful registration.
 * @param {boolean} props.isOpen - Indicates whether the modal is open.
 * @param {Function} props.onOpen - Callback function to execute when the modal opens.
 * @param {Function} props.onClose - Callback function to execute when the modal closes.
 * @param {boolean} [props.isPasswordlessEnabled=false] - Determines if passwordless login is enabled.
 * @param {boolean} [props.isSocialEnabled=false] - Determines if social login is enabled.
 * @param {Array} [props.idps=[]] - Array of identity providers for social login.
 * @param {boolean} [props.isRegisterDoneInCheckout=false] - Indicates if registration was completed during checkout.
 * @returns {JSX.Element} The rendered AuthModal component.
 *
 * @example
 * <AuthModal
 *   initialView="LOGIN_VIEW"
 *   initialEmail="user@example.com"
 *   onLoginSuccess={() => console.log('Login successful')}
 *   onRegistrationSuccess={() => console.log('Registration successful')}
 *   isOpen={isModalOpen}
 *   onOpen={() => setModalOpen(true)}
 *   onClose={() => setModalOpen(false)}
 *   isPasswordlessEnabled={true}
 *   isSocialEnabled={true}
 *   idps={['google', 'facebook']}
 *   isRegisterDoneInCheckout={false}
 * />
 */
export const AuthModal = ({
    initialView = LOGIN_VIEW,
    initialEmail = '',
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    isOpen,
    onOpen,
    onClose,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    isRegisterDoneInCheckout = false,
    ...props
}) => {
    const {shipToCountry} = useShipToCountry()
    const {formatMessage} = useIntl()
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    const prevAuthType = usePrevious(customerType)
    const {isUS, isOutlet} = useSiteType()
    const {site, locale} = useMultiSite()
    const location = useLocation()
    const isCheckout = /\/checkout$/.test(location?.pathname)
    const {triggerSignUpEvent, triggerLoginEvent} = useGTM()
    const {updateShopperContextAfterLogout} = useUpdateShopperContext()

    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && isRegistered}
    )

    const navigate = useNavigation()
    const [currentView, setCurrentView] = useState(initialView)
    const [liveMessage, setLiveMessage] = useState('')

    // react form object is shared between Login, Register and Reset Password Modals:
    // so email / password fields are sharing ones (email / password DOM input values keep between Modal view switching)
    const form = useForm()
    const authFormPrefix = 'account-modal_'

    const toast = useToast()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const register = useAuthHelper(AuthHelpers.Register)
    const appOrigin = useAppOrigin()

    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState(initialEmail)
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const passwordlessConfigCallback = getConfig().app.login?.passwordless?.callbackURI
    const callbackURL = isAbsoluteURL(passwordlessConfigCallback)
        ? passwordlessConfigCallback
        : `${appOrigin}${passwordlessConfigCallback}`

    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')

    const {app: appConfig} = getConfig()
    const {setCodeVerifier} = usePasswordContext()
    const api = useCommerceApi()
    const {isIPBlocked} = useContext(IPContext)
    const submitForm = async (data) => {
        form.clearErrors()

        if (isIPBlocked) {
            onClose()
            return
        }

        /**
         * Handles the navigation logic upon successful login.
         * Redirects the user to the appropriate page based on the registration context.
         *
         * @function
         * @returns {void}
         *    Navigates the user to either the checkout page if registration was completed during checkout,
         *    or to the account page if registration was completed outside of checkout.
         */
        const onLoginSuccess = () => {
            if (isRegisterDoneInCheckout) {
                navigate('/checkout')
            } else {
                navigate('/account')
            }
        }

        const handlePasswordlessLogin = async (email) => {
            try {
                const redirectPath = window.location.pathname + window.location.search
                await authorizePasswordlessLogin.mutateAsync({
                    userid: email,
                    callbackURI: `${callbackURL}?redirectUrl=${redirectPath}`
                })

                setCurrentView(EMAIL_VIEW)
            } catch (error) {
                const message = USER_NOT_FOUND_ERROR.test(error.message)
                    ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                    : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                    ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                    : formatMessage(API_ERROR_MESSAGE)
                form.setError('global', {type: 'manual', message})
            }
        }

        return {
            login: async (values) => {
                if (loginType === LOGIN_TYPES.PASSWORD) {
                    try {
                        const data = removePrefixFromKeys(values, authFormPrefix)

                        await login.mutateAsync({
                            username: data.email,
                            password: data.password
                        })

                        const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
                        // we only want to merge basket when the user is logged in as a recurring user
                        // only recurring users trigger the login mutation, new user triggers register mutation
                        // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
                        // if you change logic here, also change it in login page
                        const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
                        if (shouldMergeBasket) {
                            mergeBasket.mutate({
                                headers: {
                                    // This is not required since the request has no body
                                    // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                                    'Content-Type': 'application/json'
                                },
                                parameters: {
                                    createDestinationBasket: true
                                }
                            })
                        }
                        /* GTM Integration */
                        triggerLoginEvent()

                        await updateShopperContextAfterLogout()
                    } catch (error) {
                        const message = /Unauthorized/i.test(error.message)
                            ? formatMessage(LOGIN_ERROR)
                            : formatMessage(API_ERROR_MESSAGE)
                        form.setError('global', {type: 'manual', message})
                    }
                } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                    setPasswordlessLoginEmail(data.email)
                    await handlePasswordlessLogin(data.email)
                }
            },
            register: async (values) => {
                try {
                    const data = removePrefixFromKeys(values, authFormPrefix)
                    const phoneCountry = data.phoneCountry || data.countryCode

                    const body = {
                        customer: {
                            firstName: data.firstName,
                            lastName: data.lastName,
                            email: data.email,
                            ...(data.birthday && {birthday: data.birthday}),
                            // phone format: phone country dial & phone divided whitespace
                            ...(data.phone && {
                                phoneHome: `${findDialCodeByCountry(phoneCountry)} ${data.phone}`
                            }),
                            c_phoneHomeCountryCode: phoneCountry,
                            c_shareConsent: isUS ? true : data.shareConsent,
                            c_shareConsentDate: new Date(),
                            c_marketingConsent: data.marketingConsent,
                            c_marketingConsentDate: new Date(),
                            ...(data.marketingConsent &&
                                (isOutlet
                                    ? {c_outletNewsletter: true}
                                    : {c_fullPriceNewsletter: true})),
                            c_newsletterCountryCode: data.marketingConsent ? shipToCountry.id : '',
                            login: data.email,
                            // preferred locale is set with the site locale on account creation
                            preferredLocale: locale.id.replace('-', '_')
                        },
                        password: data.password
                    }
                    await register.mutateAsync(body)

                    triggerSignUpEvent()

                    onLoginSuccess()
                } catch (err) {
                    const json = await err.response.json()
                    if (json?.type?.includes('login-already-in-use')) {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage({
                                id: 'registration_page.error.email_already_used',
                                defaultMessage: 'This email is already registred.'
                            })
                        })
                        return
                    } else {
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage(API_ERROR_MESSAGE)
                        })
                    }
                }
            },
            password: async (values) => {
                try {
                    const codeVerifier = await helpers.createCodeVerifier()
                    const appDomain = appOrigin

                    setCodeVerifier(codeVerifier)

                    if (!appDomain) {
                        throw new Error()
                    }
                    const siteDomain = !appDomain.match(/^[a-zA-Z]+:\/\//)
                        ? `https://${appOrigin}`
                        : appDomain
                    const resetPasswordCallback =
                        appConfig.login?.resetPassword?.callbackURI ?? '/reset-password-callback'
                    const callbackUri = isAbsoluteURL(resetPasswordCallback)
                        ? resetPasswordCallback
                        : `${siteDomain}${resetPasswordCallback}`
                    const data = removePrefixFromKeys(values, authFormPrefix)
                    const response = await api.shopperLogin.getPasswordResetToken(
                        {
                            body: {
                                channel_id: site.id,
                                client_id: appConfig.commerceAPI.parameters.clientId,
                                code_challenge: await helpers.generateCodeChallenge(codeVerifier),
                                user_id: data.email,
                                mode: 'callback',
                                callback_uri: callbackUri + `?siteId=${site.id}`,
                                locale: locale.id
                            }
                        },
                        // Set to true to return entire Response object instead of DTO
                        true
                    )

                    if (!response?.ok || response?.status !== 200) {
                        throw new Error()
                    }

                    onClose()
                    navigate('/set-new-password')
                } catch (e) {
                    const message =
                        e.response?.status === 400
                            ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                            : formatMessage(API_ERROR_MESSAGE)
                    form.setError('global', {type: 'manual', message})
                }
            },
            email: async () => {
                await handlePasswordlessLogin(passwordlessLoginEmail)
            }
        }[currentView](data)
    }

    // Reset form and local state when opening the modal
    useEffect(() => {
        if (isOpen) {
            setLoginType(LOGIN_TYPES.PASSWORD)
            setCurrentView(initialView)
            form.reset()
        }
    }, [isOpen])

    // Auto-focus the first field in each form view
    useEffect(() => {
        const initialField = {
            [LOGIN_VIEW]: 'email',
            [REGISTER_VIEW]: 'firstName',
            [PASSWORD_VIEW]: 'email'
        }[currentView]
        const fieldsRef = form.control?.fieldsRef?.current
        fieldsRef?.[initialField]?.ref.focus()
    }, [form.control?.fieldsRef?.current])

    // Clear form state when changing views
    useEffect(() => {
        form.reset()

        // update live message for screen readers
        setLiveMessage(formatMessage(AUTH_MODAL_VIEWS[currentView].liveMessage))
    }, [currentView])

    useEffect(() => {
        setPasswordlessLoginEmail(initialEmail)
    }, [initialEmail])

    useEffect(() => {
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const isNowRegistered = isOpen && isRegistered && (loggingIn || registering)
        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // We are done with the modal.
        onClose()

        // Show a toast only for those registed users returning to the site.
        if (loggingIn) {
            toast({
                title: `${formatMessage(
                    {
                        defaultMessage: 'Welcome {name},',
                        id: 'auth_modal.info.welcome_user'
                    },
                    {
                        name: customer.data?.firstName || ''
                    }
                )}`,
                description: `${formatMessage({
                    defaultMessage: "You're now signed in.",
                    id: 'auth_modal.description.now_signed_in'
                })}`,
                status: 'info'
            })

            // Execute action to be performed on successful login
            onLoginSuccess()
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }
    }, [isRegistered])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? onClose() : setCurrentView(LOGIN_VIEW)

    return (
        <Modal
            size="small"
            data-testid="sf-auth-modal"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            {...props}
            isCentered
        >
            <ModalOverlay />
            <ModalContent>
                {/* For screen reader: announce about modal type change */}
                <VisuallyHidden role="alert">{liveMessage}</VisuallyHidden>

                <ModalHeader pr={34}>
                    <Heading as="h1" size="4xl" textTransform="uppercase">
                        {AUTH_MODAL_VIEWS[currentView].heading}
                    </Heading>
                </ModalHeader>

                <ModalCloseButton
                    aria-label={formatMessage(AUTH_MODAL_VIEWS[currentView].ariaLabel)}
                >
                    <CloseIcon
                        boxSize={5}
                        svgTitle={formatMessage(AUTH_MODAL_VIEWS[currentView].ariaLabel)}
                        svgDescription={formatMessage(AUTH_MODAL_VIEWS[currentView].ariaLabel)}
                    />
                </ModalCloseButton>

                <ModalBody pt={32} pb={0}>
                    {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                        <LoginForm
                            form={form}
                            prefix={authFormPrefix}
                            submitForm={submitForm}
                            isFirstInputFocus={true}
                            clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                            handlePasswordlessLoginClick={() =>
                                setLoginType(LOGIN_TYPES.PASSWORDLESS)
                            }
                            handleForgotPasswordClick={() => setCurrentView(PASSWORD_VIEW)}
                            isPasswordlessEnabled={isPasswordlessEnabled}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                            clickTrackOrder={onClose}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
                        <RegisterForm
                            form={form}
                            prefix={authFormPrefix}
                            submitForm={submitForm}
                            isFirstInputFocus={true}
                            clickSignIn={onBackToSignInClick}
                            isCheckout={isCheckout}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <ResetPasswordForm
                            form={form}
                            prefix={authFormPrefix}
                            submitForm={submitForm}
                            isFirstInputFocus={true}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {currentView === EMAIL_VIEW && (
                        <PasswordlessEmailConfirmation
                            form={form}
                            submitForm={submitForm}
                            email={passwordlessLoginEmail}
                        />
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

/**
 * PropTypes definition for the AuthModal component, specifying the expected types
 * and requirements for each prop used within the component.
 *
 * @property {string} initialView - The initial view to display in the modal, must be one of
 *                                  LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW, or EMAIL_VIEW.
 * @property {string} initialEmail - The initial email to pre-fill in the modal, if applicable.
 * @property {boolean} isOpen - Indicates whether the modal is currently open. This prop is required.
 * @property {function} onOpen - Callback function to be called when the modal is opened. This prop is required.
 * @property {function} onClose - Callback function to be called when the modal is closed. This prop is required.
 * @property {function} onLoginSuccess - Callback function to be called upon successful login.
 * @property {function} onRegistrationSuccess - Callback function to be called upon successful registration.
 * @property {boolean} isPasswordlessEnabled - Indicates if passwordless authentication is enabled.
 * @property {boolean} isSocialEnabled - Indicates if social login options are enabled.
 * @property {Array<string>} idps - An array of strings representing the identity providers available for social login.
 * @property {boolean} isRegisterDoneInCheckout - Indicates if the registration process is completed during checkout.
 */
AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW, EMAIL_VIEW]),
    initialEmail: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    isRegisterDoneInCheckout: PropTypes.bool
}

/**
 *
 * @param {('register'|'login'|'password'|'email')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {passwordless = {}, social = {}} = getConfig().app.login || {}

    return {
        initialView,
        isOpen,
        onOpen,
        onClose,
        isPasswordlessEnabled: !!passwordless?.enabled,
        isSocialEnabled: !!social?.enabled,
        idps: social?.idps
    }
}
